import React, { FC, useEffect, useMemo, useState } from 'react';
import { Chess, Square } from 'chess.js';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useInterval } from 'react-use';
import classes from './ChessBoard.module.scss';
import { ActionDTO, PlayerDTO } from '@envclient/envcore/src/types/core';
import { BestActionDTO, GameEventDTO } from '@envclient/envcore';
import Button from '../../components/Button';
import ChessPiece from './ChessPiece';
import { RootState } from '../../reducers/store';
import { consumeGameEvent } from '../../reducers/gameActions';
import {
  CHESS_START_FEN,
  ChessActionDTO,
  ChessColor,
  ChessOutcome
} from '@envclient/envengine/src/envs/chess/ChessTypes';

// how long the bot client waits before auto-playing its computed move (lets the
// opponent see it as a natural "thinking" pause; the server worker latency adds to this)
const BOT_MOVE_DELAY = 1200;

interface Props {
  active: boolean;
  onSubmit: (action?: ActionDTO) => void;
  clone: { [key: string]: unknown };
  myPlayer: PlayerDTO;
  players: PlayerDTO[];
  gameEnded?: boolean;
}

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const PROMOTION_PIECES = ['q', 'r', 'b', 'n'];

const ChessBoard: FC<Props> = ({ active, onSubmit, clone, myPlayer, players, gameEnded }) => {
  const baseFen = (clone?.fen as string) || CHESS_START_FEN;
  // game is over: either via a chess outcome (e.g. 'timeout', which the local engine can't
  // detect) or because the server ended the game for another reason (e.g. a player left)
  const ended = gameEnded || (!!clone?.outcome && clone.outcome !== 'ongoing');
  // first player to join is white, second is black
  const myColor: ChessColor = players?.[0]?.id === myPlayer?.id ? 'w' : 'b';

  const dispatch = useDispatch();
  const events = useSelector((state: RootState) => state.game.events) as unknown as GameEventDTO[];

  const [selected, setSelected] = useState<Square | null>(null);
  const [tentative, setTentative] = useState<{ from: Square; to: Square; promotion?: string } | null>(null);
  const [promotion, setPromotion] = useState<{ from: Square; to: Square } | null>(null);
  // a bot move computed server-side, queued to be auto-played by this (active bot) client
  const [pendingBest, setPendingBest] = useState<ChessActionDTO | null>(null);

  // a new position arrived from the server -> clear local interaction
  useEffect(() => {
    setSelected(null);
    setTentative(null);
    setPromotion(null);
    setPendingBest(null);
  }, [baseFen]);

  const baseGame = useMemo(() => new Chess(baseFen), [baseFen]);

  const displayGame = useMemo(() => {
    const g = new Chess(baseFen);
    if (tentative) {
      try {
        g.move(tentative);
      } catch {
        // ignore - only legal targets are clickable
      }
    }
    return g;
  }, [baseFen, tentative]);

  const isMyTurn = active && baseGame.turn() === myColor && !baseGame.isGameOver() && !ended;

  // BEST_ACTION: the server computed this bot's move. Only the active (bot) client
  // auto-plays it; the action already carries the full resulting state, so we just
  // show it on the board and submit it after a short "thinking" delay.
  useEffect(() => {
    if (!events || events.length === 0) return;
    const bestActionEvent = events.find((e) => e.type === 'BEST_ACTION') as BestActionDTO | undefined;
    if (!bestActionEvent) return;
    dispatch(consumeGameEvent(bestActionEvent));
    if (!isMyTurn) return;
    const action = bestActionEvent.bestAction as ChessActionDTO;
    setPendingBest(action);
    if (action.lastMove) {
      setSelected(null);
      setTentative({ from: action.lastMove.slice(0, 2) as Square, to: action.lastMove.slice(2, 4) as Square });
    }
  }, [events, dispatch, isMyTurn]);

  useInterval(
    function submitBestAction() {
      if (pendingBest) onSubmit(pendingBest);
      setPendingBest(null);
    },
    pendingBest ? BOT_MOVE_DELAY : null
  );

  const legalMoves = useMemo(
    () => (selected ? baseGame.moves({ square: selected, verbose: true }) : []),
    [baseGame, selected]
  );
  const targets = useMemo(() => new Set(legalMoves.map((m) => m.to)), [legalMoves]);
  const captureTargets = useMemo(
    () => new Set(legalMoves.filter((m) => m.captured || m.flags.includes('e')).map((m) => m.to)),
    [legalMoves]
  );

  const ranks = myColor === 'w' ? [8, 7, 6, 5, 4, 3, 2, 1] : [1, 2, 3, 4, 5, 6, 7, 8];
  const files = myColor === 'w' ? FILES : [...FILES].reverse();

  const handleSquareClick = (square: Square) => {
    if (!isMyTurn || promotion) return;
    const piece = baseGame.get(square);

    // (re)select one of my pieces - this resets any tentative move
    if (piece && piece.color === myColor) {
      setTentative(null);
      setSelected(square);
      return;
    }
    // drop onto a highlighted legal target
    if (selected && targets.has(square)) {
      const needsPromotion = legalMoves.some((m) => m.to === square && m.promotion);
      if (needsPromotion) {
        setPromotion({ from: selected, to: square });
      } else {
        setTentative({ from: selected, to: square });
        setSelected(null);
      }
      return;
    }
    setSelected(null);
  };

  const choosePromotion = (piece: string) => {
    if (!promotion) return;
    setTentative({ from: promotion.from, to: promotion.to, promotion: piece });
    setPromotion(null);
    setSelected(null);
  };

  const handleReset = () => {
    setSelected(null);
    setTentative(null);
    setPromotion(null);
  };

  const handleSubmit = () => {
    if (!isMyTurn || !tentative) return;
    const g = new Chess(baseFen);
    try {
      g.move(tentative);
    } catch {
      return;
    }
    let outcome: ChessOutcome = 'ongoing';
    if (g.isCheckmate()) outcome = 'checkmate';
    else if (g.isStalemate()) outcome = 'stalemate';
    else if (g.isDraw() || g.isInsufficientMaterial() || g.isThreefoldRepetition()) outcome = 'draw';

    const action: ChessActionDTO = {
      fen: g.fen(),
      outcome,
      winnerColor: outcome === 'checkmate' ? myColor : undefined,
      lastMove: tentative.from + tentative.to
    };
    onSubmit(action);
    handleReset();
  };

  const statusText = baseGame.isCheckmate()
    ? 'Checkmate'
    : baseGame.isStalemate()
    ? 'Stalemate'
    : baseGame.isDraw()
    ? 'Draw'
    : clone?.outcome === 'timeout'
    ? 'Time out — game over'
    : ended
    ? 'Game over'
    : isMyTurn
    ? tentative
      ? 'Confirm your move'
      : baseGame.inCheck()
      ? 'Your move — check!'
      : 'Your move'
    : 'Waiting for opponent…';

  return (
    <div className={classes.chessCont}>
      <div className={classes.status}>{statusText}</div>
      <div className={classes.board}>
        {ranks.map((rank) =>
          files.map((file) => {
            const square = `${file}${rank}` as Square;
            const piece = displayGame.get(square);
            const fileNum = file.charCodeAt(0) - 97;
            const isLight = (fileNum + (rank - 1)) % 2 === 1;
            const isTarget = targets.has(square);
            const isCapture = isTarget && captureTargets.has(square);
            const isLast = !!tentative && (tentative.from === square || tentative.to === square);
            const cls = classNames(classes.square, {
              [classes.light]: isLight,
              [classes.dark]: !isLight,
              [classes.selected]: selected === square,
              [classes.lastMove]: isLast,
              [classes.target]: isTarget && !isCapture,
              [classes.targetCapture]: isCapture,
              [classes.clickable]: isMyTurn
            });
            return (
              <div key={square} className={cls} onClick={() => handleSquareClick(square)}>
                {piece && <ChessPiece type={piece.type} color={piece.color} />}
              </div>
            );
          })
        )}
        {promotion && (
          <div className={classes.promotionOverlay}>
            <div className={classes.promotionPanel}>
              {PROMOTION_PIECES.map((p) => (
                <div key={p} className={classes.promotionPiece} onClick={() => choosePromotion(p)}>
                  <ChessPiece type={p} color={myColor} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={classes.btnBar}>
        <Button secondary onClick={handleReset} disabled={!tentative}>
          Reset
        </Button>
        <Button onClick={handleSubmit} disabled={!isMyTurn || !tentative}>
          Submit
        </Button>
      </div>
    </div>
  );
};

export default ChessBoard;
